import React, { useState } from 'react'

/**
 * CitizenResults — Vista clara y amigable para el ciudadano
 *
 * Traduce los datos del motor a respuestas concretas:
 * "¿Cuánto voy a cobrar?", "¿Qué me falta?", "¿Qué opciones tengo?"
 *
 * Props: retorno completo de useCitizenData()
 */
export default function CitizenResults({
  nombre,
  edad,
  sexo,
  edadJubilatoria,
  aniosAporte,
  aniosFaltantes,
  puedeJubilarse,
  haberCalculado,
  haberActual,
  diferencial,
  retroactivo,
  gapsTotal,
  gapsPre94,
  checklist,
  escenarios,
}) {
  const [selectedOption, setSelectedOption] = useState(null)
  const [faqOpen, setFaqOpen] = useState({})
  const fmt = v => (typeof v === 'number' ? Math.round(v).toLocaleString('es-AR') : '0')

  const toggleFaq = id => setFaqOpen(prev => ({ ...prev, [id]: !prev[id] }))

  const heroBadge = () => {
    if (puedeJubilarse && diferencial > 0) return { text: '⚠️ Este número podría ser mayor — más abajo te contamos por qué', className: 'bg-amber-50 text-amber-700 border border-amber-200' }
    if (puedeJubilarse) return { text: '✅ Con tus aportes actuales, podés iniciar el trámite', className: 'bg-green-50 text-green-700 border border-green-200' }
    return { text: 'ℹ️ Todavía no cumplís todos los requisitos, pero tenés opciones', className: 'bg-blue-50 text-blue-700 border border-blue-200' }
  }

  const badge = heroBadge()

  const iconMap = {
    ok: { symbol: '✓', bg: 'bg-green-100 text-green-700' },
    pending: { symbol: '!', bg: 'bg-amber-100 text-amber-700' },
    missing: { symbol: '✕', bg: 'bg-red-100 text-red-600' },
  }

  const colorMap = {
    green: { bg: 'bg-green-100' },
    blue: { bg: 'bg-blue-100' },
    orange: { bg: 'bg-amber-100' },
    red: { bg: 'bg-red-100' },
  }

  const faqs = [
    {
      id: 'gaps',
      pregunta: '¿Qué significa "períodos sin registro"?',
      respuesta: (
        <span>
          No te preocupes — es muy común. ANSES no tiene digitalizados los sueldos de antes de 1994 para la mayoría de las personas.{' '}
          <strong>No te impide jubilarte</strong>, pero puede hacer que el cálculo inicial sea más bajo.{' '}
          Un abogado previsional puede ayudarte a reconstruir esos salarios con recibos, certificaciones de servicios o testigos.
        </span>
      ),
    },
    {
      id: 'esperar-vs-comprar',
      pregunta: '¿Conviene más esperar o comprar años?',
      respuesta: (
        <span>
          Depende de tu situación. <strong>Esperar</strong> te da un haber más alto porque sumás años de salario actualizado.{' '}
          <strong>Comprar años</strong> te permite jubilarte antes pero con un haber menor.{' '}
          La opción más conveniente depende de si podés seguir trabajando, tu salud y tus necesidades económicas actuales.
          Te recomendamos consultarlo con un abogado previsional.
        </span>
      ),
    },
    {
      id: 'reajuste',
      pregunta: 'Ya cobro una jubilación, ¿puedo pedir un reajuste?',
      respuesta: (
        <span>
          <strong>Sí, es posible.</strong> Muchas personas jubiladas están cobrando menos de lo que les corresponde porque:
          <ul className="mt-2 list-disc pl-5 text-sm space-y-1">
            <li>No se aplicaron los índices de actualización correctos (ISBIC, RIPTE)</li>
            <li>No se consideraron años de aportes previos a 1994</li>
            <li>Corresponde la aplicación de algún fallo judicial (ej: "Pinto", "Cardozo", "Badosa")</li>
          </ul>
        </span>
      ),
    },
    {
      id: 'documentos',
      pregunta: '¿Qué documentos necesito para empezar?',
      respuesta: (
        <span>
          Para un análisis completo, necesitás:<br /><br />
          📄 <strong>Historial laboral</strong> (que ya lo subiste — ¡gracias!)<br />
          🆔 <strong>DNI</strong><br />
          📋 <strong>Recibos de sueldo recientes</strong> (si trabajás en relación de dependencia)<br />
          🏛️ <strong>Certificaciones de servicios</strong> (si trabajaste en blanco en lugares que ya no existen)<br /><br />
          Con lo que ya subiste podemos darte un panorama. Para el trámite formal, un abogado te va a guiar con todo.
        </span>
      ),
    },
  ]

  return (
    <div className="w-full max-w-3xl mx-auto">
      {/* ====== HERO: the big number ====== */}
      <div className="bg-white rounded-2xl p-8 md:p-9 shadow-sm border border-gray-100 mb-4">
        <p className="text-sm text-gray-500 mb-2">Con tus aportes registrados, hoy podrías cobrar</p>
        <div className="text-5xl md:text-6xl font-extrabold tracking-tight text-gray-900 leading-tight">
          <span className="text-2xl md:text-3xl font-semibold align-top">$</span>
          {fmt(haberCalculado)}
          <span className="text-lg md:text-xl font-normal text-gray-400"> por mes</span>
        </div>
        <div className={`inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-full text-sm font-medium ${badge.className}`}>
          {badge.text}
        </div>
      </div>

      {/* ====== QUICK FACTS ====== */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
          <div className="text-2xl font-bold text-gray-900">{edad}</div>
          <div className="text-xs text-gray-500 mt-1">Tu edad</div>
          {edad < edadJubilatoria && (
            <div className="text-[11px] text-gray-400 mt-0.5">Te faltan {edadJubilatoria - edad} años</div>
          )}
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
          <div className="text-2xl font-bold text-gray-900">{aniosAporte}</div>
          <div className="text-xs text-gray-500 mt-1">Años con aportes</div>
          <div className="text-[11px] text-gray-400 mt-0.5">Registrados en ANSES</div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
          <div className="text-2xl font-bold text-gray-900">${fmt(haberCalculado)}</div>
          <div className="text-xs text-gray-500 mt-1">Tu haber estimado</div>
          <div className="text-[11px] text-gray-400 mt-0.5">Según tus aportes actuales</div>
        </div>
      </div>

      {/* ====== CHECKLIST ====== */}
      <h3 className="text-lg font-bold text-gray-900 mb-1">📋 Tu situación hoy</h3>
      <p className="text-sm text-gray-500 mb-4">Esto es lo que necesitás saber para tomar una decisión.</p>

      <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 mb-6">
        {checklist.length === 0 ? (
          <p className="text-sm text-gray-400">No hay información suficiente para mostrar el checklist.</p>
        ) : (
          checklist.map((item, i) => {
            const ic = iconMap[item.icon] || iconMap.pending
            return (
              <div key={i} className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5 ${ic.bg}`}>
                  {ic.symbol}
                </div>
                <div className="text-sm text-gray-800">
                  {item.titulo}
                  {item.detalle && <span className="block text-xs text-gray-400 mt-0.5">{item.detalle}</span>}
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* ====== OPTIONS / SCENARIOS ====== */}
      <h3 className="text-lg font-bold text-gray-900 mb-1">🔀 Tus opciones</h3>
      <p className="text-sm text-gray-500 mb-4">Elegí una opción para ver cómo cambiaría tu jubilación.</p>

      {escenarios.length === 0 ? (
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 mb-6">
          <p className="text-sm text-gray-400">No hay opciones disponibles para tu situación actual.</p>
        </div>
      ) : (
        escenarios.map((opt) => {
          const cc = colorMap[opt.color] || colorMap.blue
          const isSelected = selectedOption === opt.id
          return (
            <div
              key={opt.id}
              onClick={() => setSelectedOption(opt.id)}
              className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all duration-150 mb-3
                ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/30'}`}
            >
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-lg flex-shrink-0 ${cc.bg}`}>
                {opt.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-900">{opt.titulo}</div>
                <div className="text-xs text-gray-500 mt-0.5">{opt.descripcion}</div>
              </div>
              <div className={`text-base font-bold flex-shrink-0 ${opt.etiquetaValor?.includes('→') ? 'text-gray-900' : 'text-green-700'}`}>
                {opt.etiquetaValor}
                {opt.esEstimado && <span className="block text-[10px] font-normal text-gray-400 text-right">estimado</span>}
              </div>
            </div>
          )
        })
      )}

      {/* ====== FAQ ====== */}
      <h3 className="text-lg font-bold text-gray-900 mb-4 mt-6">❓ Preguntas frecuentes</h3>

      {faqs.map(faq => (
        <div key={faq.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-3">
          <button
            onClick={() => toggleFaq(faq.id)}
            className="w-full px-5 py-4 flex justify-between items-center text-sm font-medium text-gray-800 cursor-pointer hover:bg-gray-50 transition-colors"
          >
            <span>{faq.pregunta}</span>
            <svg
              className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${faqOpen[faq.id] ? 'rotate-180' : ''}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {faqOpen[faq.id] && (
            <div className="px-5 pb-4 text-sm text-gray-600 leading-relaxed">
              {faq.respuesta}
            </div>
          )}
        </div>
      ))}

      {/* ====== CTA ====== */}
      <div className="bg-gray-900 rounded-2xl p-6 md:p-8 text-center text-white mt-6">
        <h3 className="text-lg font-bold mb-1">¿Querés saber exactamente cuánto podrías cobrar?</h3>
        <p className="text-sm text-gray-400 mb-4">
          Esto es un panorama general. Un abogado previsional puede analizar tu caso en detalle
          y decirte exactamente qué opción te conviene más.
        </p>
        <button
          type="button"
          className="bg-white text-gray-900 px-8 py-3 rounded-xl text-sm font-semibold hover:bg-gray-100 transition-colors"
        >
          🔍 Consultar con un abogado
        </button>
      </div>

      {/* spacer for scroll */}
      <div className="h-8" />
    </div>
  )
}
